import {
    useState,
    useEffect,
    useCallback,
    useContext,
    useRef,
    createContext,
    ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────
const ONBOARDING_MODAL_KEY = "reqtangle-onboarding-dismissed";
const ONBOARDING_TOUR_KEY  = "reqtangle-onboarding-tour-step";

export const ONBOARDING_TOUR_TOTAL = 24;
export const ONBOARDING_TOUR_DISPLAY_TOTAL = 21;

/** Steps whose anchor lives inside the sidebar drawer on mobile.
 *  TourProvider will call setOpen(true) before the tooltip tries to measure. */
const SIDEBAR_STEPS = new Set([14, 16, 19, 20, 24]);

/** Steps that advance by user action (tap element) rather than a Next button.
 *  On mobile we always render a Next fallback so the user can't get stuck.
 *  Steps 2 and 3 are excluded — they advance via the dialog's own buttons. */
const ACTION_STEPS = new Set([4, 5, 7, 10, 11, 14, 16, 17, 19, 20, 22, 24]);

// ─── Mobile detection ─────────────────────────────────────────────────────────

const MOBILE_BREAKPOINT = 768;

function useIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState(
        () => window.innerWidth < MOBILE_BREAKPOINT
    );
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        setIsMobile(mq.matches);
        return () => mq.removeEventListener("change", handler);
    }, []);
    return isMobile;
}

// ─── Tour Context ─────────────────────────────────────────────────────────────

interface TourContextValue {
    tourStep: number | null;
    setTourStep: (step: number | null) => void;
    advanceTour: () => void;
    dismissTour: () => void;
    showCompletion: boolean;
    dismissCompletion: () => void;
    /** Dashboard calls this once to register a function that opens the sidebar drawer. */
    registerSidebarOpener: (fn: () => void) => void;
}

const TourContext = createContext<TourContextValue | null>(null);

/**
 * Wrap your app (or at minimum the subtree containing OnboardingModal,
 * allProjects, and createNewProject) with this provider.
 *
 * Example in App.tsx:
 *   <TourProvider>
 *     <RouterProvider ... />
 *   </TourProvider>
 */
export const TourProvider = ({ children }: { children: ReactNode }) => {
    const [showCompletion, setShowCompletion] = useState(false);
    const sidebarOpenerRef = useRef<(() => void) | null>(null);

    const [tourStep, setTourStepState] = useState<number | null>(() => {
        const stored = localStorage.getItem(ONBOARDING_TOUR_KEY);
        return stored !== null ? Number(stored) : null;
    });

    const setTourStep = useCallback((step: number | null) => {
        if (step === null) {
            localStorage.removeItem(ONBOARDING_TOUR_KEY);
        } else {
            localStorage.setItem(ONBOARDING_TOUR_KEY, String(step));
        }
        setTourStepState(step);
    }, []);

    const advanceTour = useCallback(() => {
        setTourStepState((prev) => {
            if (prev === null) return null;
            const next = prev + 1;
            if (next > ONBOARDING_TOUR_TOTAL) {
                localStorage.removeItem(ONBOARDING_TOUR_KEY);
                setShowCompletion(true);
                return null;
            }
            localStorage.setItem(ONBOARDING_TOUR_KEY, String(next));
            return next;
        });
    }, []);

    const dismissTour = useCallback(() => {
        localStorage.removeItem(ONBOARDING_TOUR_KEY);
        setTourStepState(null);
    }, []);

    const dismissCompletion = useCallback(() => {
        setShowCompletion(false);
    }, []);

    const registerSidebarOpener = useCallback((fn: () => void) => {
        sidebarOpenerRef.current = fn;
    }, []);

    // On mobile, auto-open the sidebar drawer when we land on a sidebar-anchored step.
    // Delay 350 ms so the Sheet open animation (~300 ms) fully completes before
    // MobileTooltip starts polling for the anchor element's bounding rect.
    const isMobile = useIsMobile();
    const prevStep = useRef<number | null>(null);
    useEffect(() => {
        if (!isMobile) return;
        if (tourStep === null) return;
        if (tourStep === prevStep.current) return; // already handled this step
        prevStep.current = tourStep;

        if (SIDEBAR_STEPS.has(tourStep) && sidebarOpenerRef.current) {
            const id = setTimeout(() => sidebarOpenerRef.current?.(), 350);
            return () => clearTimeout(id);
        }
    }, [tourStep, isMobile]);

    return (
        <TourContext.Provider value={{
            tourStep, setTourStep, advanceTour, dismissTour,
            showCompletion, dismissCompletion, registerSidebarOpener,
        }}>
            {children}
            {showCompletion && <OnboardingCompletionModal onClose={dismissCompletion} />}
        </TourContext.Provider>
    );
};

/**
 * Hook for consuming the tour state.
 * Must be used inside <TourProvider>.
 */
export const useOnboardingTour = (): TourContextValue => {
    const ctx = useContext(TourContext);
    if (!ctx) throw new Error("useOnboardingTour must be used inside <TourProvider>");
    return ctx;
};

// ─── OnboardingTooltip ────────────────────────────────────────────────────────

const TOOLTIP_W = 288;

type TooltipSide = "top" | "bottom" | "left" | "right";

interface OnboardingTooltipProps {
    anchorId: string;
    step: number;
    displayStep?: number;
    title: string;
    description: React.ReactNode;
    side?: TooltipSide;
    onDismiss: () => void;
    onNext?: () => void;
    className?: string;
}

// ─── Desktop tooltip (unchanged behaviour) ────────────────────────────────────

const DesktopTooltip = ({
    anchorId,
    step,
    displayStep,
    title,
    description,
    side = "bottom",
    onDismiss,
    onNext,
    className,
}: OnboardingTooltipProps) => {
    const [layout, setLayout] = useState<{
        tooltip: { top: number; left: number };
        anchor: { top: number; left: number; width: number; height: number };
    } | null>(null);

    useEffect(() => {
        let retryId: ReturnType<typeof setTimeout> | null = null;
        const GAP = 14;

        const calculate = () => {
            const anchor = document.getElementById(anchorId);
            if (!anchor) { retryId = setTimeout(calculate, 50); return; }
            const aRect = anchor.getBoundingClientRect();
            if (aRect.width === 0 && aRect.height === 0) {
                retryId = setTimeout(calculate, 50); return;
            }

            let top = 0;
            let left = 0;

            switch (side) {
                case "bottom":
                    top  = aRect.bottom + GAP;
                    left = aRect.left + aRect.width / 2 - TOOLTIP_W / 2;
                    break;
                case "top":
                    top  = aRect.top - GAP - 120;
                    left = aRect.left + aRect.width / 2 - TOOLTIP_W / 2;
                    break;
                case "right":
                    top  = aRect.top + aRect.height / 2 - 60;
                    left = aRect.right + GAP;
                    break;
                case "left":
                    top  = aRect.top + aRect.height * 0.15;
                    left = aRect.left - TOOLTIP_W - GAP;
                    break;
            }

            left = Math.max(8, Math.min(left, window.innerWidth  - TOOLTIP_W - 8));
            top  = Math.max(8, Math.min(top,  window.innerHeight - 160 - 8));

            setLayout({
                tooltip: { top, left },
                anchor: { top: aRect.top, left: aRect.left, width: aRect.width, height: aRect.height },
            });
        };

        calculate();
        window.addEventListener("resize", calculate);
        window.addEventListener("scroll", calculate, true);
        return () => {
            if (retryId) clearTimeout(retryId);
            window.removeEventListener("resize", calculate);
            window.removeEventListener("scroll", calculate, true);
        };
    }, [anchorId, side]);

    const isReady = layout !== null;
    const PAD = 8;

    return createPortal(
        <>
            {isReady && (() => {
                const { top, left, width, height } = layout.anchor;
                const vw = window.innerWidth;
                const vh = window.innerHeight;
                const r  = top    - PAD;
                const l  = left   - PAD;
                const w  = width  + PAD * 2;
                const h  = height + PAD * 2;
                const cr = 10;
                const d  = `M 0 0 H ${vw} V ${vh} H 0 Z ` +
                    `M ${l + cr} ${r} H ${l + w - cr} Q ${l + w} ${r} ${l + w} ${r + cr} ` +
                    `V ${r + h - cr} Q ${l + w} ${r + h} ${l + w - cr} ${r + h} ` +
                    `H ${l + cr} Q ${l} ${r + h} ${l} ${r + h - cr} ` +
                    `V ${r + cr} Q ${l} ${r} ${l + cr} ${r} Z`;
                return (
                    <svg
                        style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", zIndex: 9998, pointerEvents: "none" }}
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d={d} fill="rgba(0,0,0,0.45)" fillRule="evenodd" />
                    </svg>
                );
            })()}

            <div
                style={
                    isReady
                        ? { top: layout.tooltip.top, left: layout.tooltip.left, width: TOOLTIP_W }
                        : { visibility: "hidden", top: 0, left: 0, width: TOOLTIP_W }
                }
                className={`fixed z-[9999] bg-gradient-to-r from-gray-200 via-gray-100 to-white rounded-xl shadow-2xl border-1 border-[#7304FB] p-5 ${className ?? ""}`}
            >
                <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-[--req-purple] font-semibold text-md leading-snug">{title}</p>
                    <button
                        onClick={onDismiss}
                        className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 mt-0.5"
                        aria-label="Dismiss onboarding tour"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-3 whitespace-pre-line">{description}</p>

                <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-gray-400 font-medium">{displayStep ?? step}/{ONBOARDING_TOUR_DISPLAY_TOTAL}</span>
                    {onNext && (
                        <button
                            onClick={onNext}
                            className="text-xs font-semibold text-white bg-[var(--req-purple,#7304FB)] px-4 py-1.5 rounded-lg hover:opacity-90 transition-opacity"
                        >
                            Next
                        </button>
                    )}
                </div>
            </div>
        </>,
        document.body
    );
};

// ─── Mobile bottom-sheet tooltip ──────────────────────────────────────────────

const MobileTooltip = ({
    anchorId,
    step,
    displayStep,
    title,
    description,
    onDismiss,
    onNext,
}: OnboardingTooltipProps) => {
    const [anchorRect, setAnchorRect] = useState<{
        top: number; left: number; width: number; height: number;
    } | null>(null);

    useEffect(() => {
        let retryId: ReturnType<typeof setTimeout> | null = null;

        const calculate = () => {
            const anchor = document.getElementById(anchorId);
            if (!anchor) { retryId = setTimeout(calculate, 80); return; }
            const rect = anchor.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0) {
                retryId = setTimeout(calculate, 80); return;
            }
            setAnchorRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        };

        calculate();
        window.addEventListener("resize", calculate);
        window.addEventListener("scroll", calculate, true);
        return () => {
            if (retryId) clearTimeout(retryId);
            window.removeEventListener("resize", calculate);
            window.removeEventListener("scroll", calculate, true);
        };
    }, [anchorId]);

    const { advanceTour } = useOnboardingTour();

    // Full-screen anchor detection: when the anchor element covers most of the
    // viewport (e.g. a Dialog), the cutout SVG would be useless. Skip it and
    // show a "use the form button" hint instead.
    const isFullScreenAnchor = anchorRect !== null &&
        anchorRect.width  > window.innerWidth  * 0.5 &&
        anchorRect.height > window.innerHeight * 0.4;

    // Action steps have no explicit onNext — on mobile we always add a Next button
    // so the user isn't stranded if they can't reach the highlighted element.
    const isActionStep = ACTION_STEPS.has(step) && !onNext;
    const showNextButton = !!onNext || isActionStep;
    const handleNext = onNext ?? advanceTour;

    // Smart card position: opposite side from the highlighted element.
    // Anchor in lower half → card at top. Anchor in upper half → card at bottom.
    // Full-screen anchors always get the card at top.
    const anchorMidY = anchorRect ? anchorRect.top + anchorRect.height / 2 : 0;
    const cardAtTop  = isFullScreenAnchor || (anchorRect !== null && anchorMidY > window.innerHeight / 2);

    const MARGIN = 16;
    const sheetStyle: React.CSSProperties = cardAtTop
        ? { position: "fixed", top: MARGIN, left: MARGIN, right: MARGIN, zIndex: 9999 }
        : { position: "fixed", bottom: MARGIN, left: MARGIN, right: MARGIN, zIndex: 9999 };

    // SVG cutout path — omitted for full-screen anchors
    const PAD = 8;
    const overlayPath = (!isFullScreenAnchor && anchorRect) ? (() => {
        const { top, left, width, height } = anchorRect;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const r  = top    - PAD;
        const l  = left   - PAD;
        const w  = width  + PAD * 2;
        const h  = height + PAD * 2;
        const cr = 10;
        return `M 0 0 H ${vw} V ${vh} H 0 Z ` +
            `M ${l + cr} ${r} H ${l + w - cr} Q ${l + w} ${r} ${l + w} ${r + cr} ` +
            `V ${r + h - cr} Q ${l + w} ${r + h} ${l + w - cr} ${r + h} ` +
            `H ${l + cr} Q ${l} ${r + h} ${l} ${r + h - cr} ` +
            `V ${r + cr} Q ${l} ${r} ${l + cr} ${r} Z`;
    })() : null;

    return createPortal(
        <>
            {/* Dim overlay with cutout (skipped for full-screen anchors) */}
            {overlayPath && (
                <svg
                    style={{ position: "fixed", inset: 0, width: "100vw", height: "100vh", zIndex: 9998, pointerEvents: "none" }}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d={overlayPath} fill="rgba(0,0,0,0.5)" fillRule="evenodd" />
                </svg>
            )}

            {/* Bottom-sheet card */}
            <div
                style={sheetStyle}
                className="bg-gradient-to-r from-gray-200 via-gray-100 to-white rounded-2xl shadow-2xl border border-[#7304FB] p-5"
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-[--req-purple] font-semibold text-base leading-snug">{title}</p>
                    <button
                        onClick={onDismiss}
                        className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 mt-0.5"
                        aria-label="Dismiss onboarding tour"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{description}</p>

                {/* Contextual hint — mutually exclusive */}
                {isFullScreenAnchor && (
                    <p className="text-xs text-[var(--req-purple,#7304FB)] font-medium mt-2">
                        Use the button in the form below to continue.
                    </p>
                )}
                {isActionStep && !isFullScreenAnchor && (
                    <p className="text-xs text-[var(--req-purple,#7304FB)] font-medium mt-2">
                        Tap the highlighted element to continue, or tap Next to skip.
                    </p>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-gray-400 font-medium">
                        {displayStep ?? step}/{ONBOARDING_TOUR_DISPLAY_TOTAL}
                    </span>
                    {showNextButton && (
                        <button
                            onClick={handleNext}
                            className="text-sm font-semibold text-white bg-[var(--req-purple,#7304FB)] px-5 py-2 rounded-lg hover:opacity-90 transition-opacity"
                        >
                            Next
                        </button>
                    )}
                </div>
            </div>
        </>,
        document.body
    );
};

// ─── Public OnboardingTooltip — routes to desktop or mobile ──────────────────

export const OnboardingTooltip = (props: OnboardingTooltipProps) => {
    const isMobile = useIsMobile();
    return isMobile
        ? <MobileTooltip {...props} />
        : <DesktopTooltip {...props} />;
};

// ─── Completion Modal ─────────────────────────────────────────────────────────

interface OnboardingCompletionModalProps {
    onClose: () => void;
}

const OnboardingCompletionModal = ({ onClose }: OnboardingCompletionModalProps) => {
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="w-[600px] max-w-[90vw]! p-0 gap-0 border-0 shadow-2xl overflow-hidden [&>button]:hidden">
                {/* max-h + overflow-y-auto lets long content scroll on small screens.
                    Reduced padding on mobile (p-6) expands to desktop size (sm:p-10). */}
                <div className="bg-white rounded-lg p-6 sm:p-10 max-h-[80vh] overflow-y-auto">
                    <div className="flex flex-col gap-4">
                        <h2 className="text-[1.5rem] sm:text-[1.75rem] font-semibold leading-snug text-[var(--req-purple,#7304FB)]">
                            Awesome work! 🎉
                        </h2>
                        <p className="text-base text-gray-700 leading-relaxed">
                            That's a wrap for this quick tour! There's so much waiting for you.
                        </p>
                        <p className="text-base text-gray-700 leading-relaxed">
                            From here you can now continue refining your project and requirements. However, to typically follow the requirements process I would suggest you to first identify your stakeholders and then the user roles for your processes. Don't miss out on creating your glossary, which is absolutely necessary for mutual language.
                        </p>
                        <p className="font-bold text-xl leading-relaxed">
                            Good luck with your project!
                        </p>
                    </div>

                    <div className="flex justify-end mt-8">
                        <Button
                            variant="default"
                            onClick={onClose}
                            className="bg-(--req-purple) text-white px-8 py-2"
                        >
                            Complete
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// ─── Intro modal ──────────────────────────────────────────────────────────────

interface OnboardingStep {
    title: string;
    content: string;
    className?: string;
}

const onboardingSteps: OnboardingStep[] = [
    {
        title: "Hi there, welcome to Reqtangle!",
        content:
            "With Reqtangle you'll manage your project requirements in a structured and effective way immediately, without switching between multiple apps.",
        className: "",
    },
    {
        title: "Let's start together!",
        content:
            "Let's kickstart your journey. We'll go through a short onboarding process together. If you ever need a break, you can exit anytime — your progress will be saved automatically.",
        className: "",
    },
];

// Fixed pixel width/height attributes removed — viewBox retained for scaling.
// className="w-full h-auto max-w-[Npx]" makes each illustration fluid on mobile.
const StepOneIllustration = () => (
    <svg
        viewBox="0 0 569 240"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto max-w-[569px]"
    >
        <g filter="url(#filter0_d_670_9563)">
            <rect x="20" y="20" width="253" height="151" rx="20" fill="white" />
            <rect x="20.5" y="20.5" width="252" height="150" rx="19.5" stroke="#DDDDE7" />
        </g>
        <g filter="url(#filter1_d_670_9563)">
            <rect x="383" y="121" width="166" height="99" rx="20" fill="white" />
            <rect x="383.5" y="121.5" width="165" height="98" rx="19.5" stroke="#DDDDE7" />
            <rect x="397" y="133" width="69" height="10" rx="5" fill="#DDDDE7" />
        </g>
        <path d="M384 175H339C336.239 175 334 172.761 334 170V100.5C334 97.7386 331.761 95.5 329 95.5H272" stroke="#DDDDE7" strokeWidth="2" />
        <rect x="32" y="36" width="110" height="18" rx="9" fill="#DDDDE7" />
        <rect x="32" y="83" width="216" height="13" rx="6.5" fill="#DDDDE7" fillOpacity="0.37" />
        <rect x="397" y="157" width="135" height="7" rx="3.5" fill="#DDDDE7" fillOpacity="0.37" />
        <defs>
            <filter id="filter0_d_670_9563" x="0" y="0" width="293" height="191" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset /><feGaussianBlur stdDeviation="10" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_670_9563" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_670_9563" result="shape" />
            </filter>
            <filter id="filter1_d_670_9563" x="363" y="101" width="206" height="139" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset /><feGaussianBlur stdDeviation="10" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_670_9563" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_670_9563" result="shape" />
            </filter>
        </defs>
    </svg>
);

const StepTwoIllustration = () => (
    <svg
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto max-w-[200px]"
    >
        <g filter="url(#checkcard_shadow)">
            <rect x="25" y="25" width="150" height="150" rx="28" fill="white" />
            <rect x="25.5" y="25.5" width="149" height="149" rx="27.5" stroke="#DDDDE7" />
        </g>
        <path d="M70 102 L88 120 L130 80" stroke="#BBBBC8" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
        <defs>
            <filter id="checkcard_shadow" x="0" y="0" width="200" height="200" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset /><feGaussianBlur stdDeviation="10" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
            </filter>
        </defs>
    </svg>
);

export const OnboardingModal = () => {
    const [isOpen, setIsOpen]           = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const { setTourStep }               = useOnboardingTour();

    useEffect(() => {
        const isDismissed = localStorage.getItem(ONBOARDING_MODAL_KEY);
        if (!isDismissed) setIsOpen(true);
    }, []);

    const handleTemporaryClose = () => setIsOpen(false);

    const handleNext = () => {
        if (currentStep < onboardingSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            localStorage.setItem(ONBOARDING_MODAL_KEY, "true");
            setTourStep(1);
            setIsOpen(false);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1);
    };

    const currentStepData = onboardingSteps[currentStep];

    return (
        <Dialog open={isOpen} onOpenChange={() => { /* blocked — only X closes */ }}>
            <DialogContent className="w-[725px] max-w-[90vw]! p-0 gap-0 border-0 shadow-2xl overflow-hidden">
                <button
                    onClick={handleTemporaryClose}
                    className="absolute right-4 top-4 z-50 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
                >
                    <X />
                    <span className="sr-only">Close</span>
                </button>

                {/*
                  pt-8 on mobile reclaims vertical space (was pt-12).
                  overflow-y-auto + max-h-[90vh] lets very short screens scroll.
                */}
                <div className="bg-gradient-to-b from-[#f1e6fe] to-white rounded-lg p-4 pt-8 sm:pt-12 overflow-y-auto max-h-[90vh]">
                    {/*
                      w-[545px] replaced with w-full max-w-[545px] so it shrinks
                      instead of overflowing on narrow screens.
                    */}
                    <div className={`w-full max-w-[545px] m-auto flex flex-col items-center ${currentStepData.className ?? ""}`}>
                        {currentStep === 0 ? <StepOneIllustration /> : <StepTwoIllustration />}

                        {/* Tighter margins on mobile, full margins on sm+ */}
                        <div className="text-center mt-6 mb-10 sm:mt-10 sm:mb-20">
                            <h2 className="text-[1.5rem] sm:text-[2rem] leading-tight font-semibold my-4 sm:my-8">
                                {currentStepData.title}
                            </h2>
                            <p className="text-base sm:text-lg">{currentStepData.content}</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-between w-full gap-4">
                        <div className="flex-1 text-left">
                            {currentStep > 0 && (
                                <Button variant="ghost" size="sm" onClick={handlePrevious} className="p-5!">
                                    Back
                                </Button>
                            )}
                        </div>
                        <div className="flex flex-1 gap-2 justify-center">
                            {onboardingSteps.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentStep(index)}
                                    className={`w-2.5 h-2.5 rounded-full transition-all ${index === currentStep ? "bg-purple-600" : "bg-gray-300 hover:bg-gray-400"}`}
                                    aria-label={`Go to step ${index + 1}`}
                                />
                            ))}
                        </div>
                        <div className="flex-1 text-right">
                            <Button
                                variant="default"
                                size="sm"
                                onClick={handleNext}
                                className="bg-(--req-purple) text-white p-5!"
                            >
                                {currentStep === onboardingSteps.length - 1 ? "Let's start" : "Next"}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};